"use client";

import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

interface AgentProfile {
  agentId: string | null;
  agentName: string | null;
  agentToken: string | null;
  agentEmails: string[];
  isDirectAgent?: boolean;
}

interface AgentIdContextType extends AgentProfile {
  isCustomAgentId: boolean;
  isDirectAgent: boolean;
  setAgentId: (id: string | null) => void;
  setDirectAgentId: (id: string | null) => void;
  setAgentProfile: (profile: AgentProfile | null) => void;
}

const AgentIdContext = createContext<AgentIdContextType | undefined>(undefined);


const setAgentTokenCookie = (token: string | null) => {
  if (typeof document === "undefined") return;

  if (!token) {
    // Löschen
    document.cookie = "AGENT_TOKEN=; path=/; max-age=0";
  } else {
    // Session-Cookie → kein max-age / expires
    document.cookie = `AGENT_TOKEN=${encodeURIComponent(token)}; path=/`;
  }
};

const createEmptyProfile = (): AgentProfile => ({
  agentId: null,
  agentName: null,
  agentToken: null,
  agentEmails: [],
  isDirectAgent: false,
});

const normalizeProfile = (profile: AgentProfile | null): AgentProfile => ({
  agentId: profile?.agentId ?? null,
  agentName: profile?.agentName ?? null,
  agentToken: profile?.agentToken ?? null,
  agentEmails: Array.isArray(profile?.agentEmails) ? profile?.agentEmails ?? [] : [],
  isDirectAgent: profile?.isDirectAgent ?? false,
});

export const AgentIdProvider: React.FC<{ children: ReactNode; initialAgentProfile: AgentProfile | null }> = ({ children, initialAgentProfile }) => {
  const [profile, setProfile] = useState<AgentProfile>(() => {
    if (initialAgentProfile) return normalizeProfile(initialAgentProfile);
    return createEmptyProfile();
  });

  const isDirectAgent = profile.isDirectAgent ?? false;
  const isCustomAgentId = !!profile.agentId && !isDirectAgent;

  const setAgentId = useCallback((id: string | null) => {
    if (!id) {
      setAgentTokenCookie(null);
      setProfile(createEmptyProfile());
    } else {
      setProfile(prev => ({ ...prev, agentId: id, isDirectAgent: false }));
    }
  }, []);

  const setDirectAgentId = useCallback((id: string | null) => {
    if (!id) {
      setAgentTokenCookie(null);
      setProfile(createEmptyProfile());
    } else {
      setProfile({
        agentId: id,
        agentName: null,
        agentToken: null,
        agentEmails: [],
        isDirectAgent: true,
      });
    }
  }, []);

  const setAgentProfile = useCallback((newProfile: AgentProfile | null) => {
    if (!newProfile) {
      setAgentTokenCookie(null);
      setProfile(createEmptyProfile());
    } else {
      setAgentTokenCookie(newProfile.agentToken ?? null);
      setProfile(normalizeProfile(newProfile));
    }
  }, []);

  const contextValue = useMemo(
    () => ({ ...profile, isDirectAgent, isCustomAgentId, setAgentId, setDirectAgentId, setAgentProfile }),
    [profile, isDirectAgent, isCustomAgentId, setAgentId, setDirectAgentId, setAgentProfile]
  );

  return (
    <AgentIdContext.Provider value={contextValue}>
      {children}
    </AgentIdContext.Provider>
  );
};

export const useAgentId = () => {
  const context = useContext(AgentIdContext);
  if (context === undefined) {
    throw new Error('useAgentId must be used within an AgentIdProvider');
  }
  return context;
};
